import { Controller } from '@hotwired/stimulus';
import { base64UrlEncode } from '../utilities/encoding';

export default class extends Controller {
    static targets = ['floating', 'data', 'counter', 'toggle', 'cart', 'overall', 'countdownText'];

    static values = {
        translatedText: String,
        expiryMinutes: Number
    }

    initialize() {
        super.initialize();
        if (window.localStorage.hasOwnProperty('__floating_cart')) {
            const storedData = JSON.parse(window.localStorage.getItem('__floating_cart'));
            this.cart = storedData.cart || {};
            this.expiryTime = storedData.expiryTime || null;
        } else {
            this.cart = {};
            this.expiryTime = null;
        }
    }

    connect() {
        super.connect();
        this.setupTeleport();

        window.floatingCart = this;
        this.update();
        this.startCountdown();

        this.floatingTarget.classList.add('d-none');

        const searchParams = new URLSearchParams(window.location.search);
        if (window.location.pathname === '/atsiskaitymo-informacija/' && searchParams.has('data')) {
            this.expireCart();
        }
    }

    add(item, groupBy = []) {
        if (groupBy.length) {
            const groupKey = groupBy.map(key => item[key]).join('_');

            if (this.cart?.[groupKey]) {
                this.cart[groupKey].quantity++;
            } else {
                item.quantity = 1;
                item._key = groupKey;
                this.cart[groupKey] = item;
            }

            if (this.expiryTime === null) {
                this.expiryTime = Date.now() + this.expiryMinutesValue * 60 * 1000;
            }

            this.update();
            this.startCountdown();
        }
    }

    update() {
        let count = 0;
        let overall = 0;
        let simplifiedCart = [];

        const isAnonymous = this.isAnonymousCheckoutAllowed();
        const formElement = this.element.querySelector('form');
        if (formElement) {
            const anonymousUrl = formElement.dataset.anonymousCheckoutUrl;
            const normalUrl = formElement.dataset.checkoutUrl;
            formElement.action = isAnonymous ? anonymousUrl : normalUrl;
        }

        Object.values(this.cart).forEach(item => {
            count += item.quantity;
            overall += (item?.price || 0) * item.quantity;

            const exclude = ['_key', 'price'];
            let simple = {};
            Object.entries(item).forEach(([key, value]) => {
                if (!exclude.includes(key)) {
                    simple[key] = value;
                }
            });

            if (Object.keys(this.cart).length > 0) {
                simplifiedCart.push(simple);
            }
        });

        let displayedCount = count > 9 ? '9+' : count;
        this.counterTarget.innerHTML = `(${displayedCount})`;

        if (count > 0) {
            this.toggleTarget.classList.remove('d-none');
        } else {
            this.toggleTarget.classList.add('d-none');
        }

        this.cartTarget.innerHTML = '';
        this.overallTarget.innerHTML = `${overall} &euro;`;

        Object.values(this.cart).forEach(item => {
            const node = document.createElement('tr');
            node.innerHTML = this.template(item);
            this.cartTarget.appendChild(node);
        });

        const cartEmpty = Object.keys(this.cart).length <= 0;

        if (cartEmpty) {
            this.floatingTarget.classList.add('d-none');
            this.dataTarget.value = '';
            this.expiryTime = null;
            window.localStorage.removeItem('__floating_cart');
        } else {
            this.floatingTarget.classList.remove('d-none');
            this.dataTarget.value = base64UrlEncode(JSON.stringify(simplifiedCart));
            window.localStorage.setItem('__floating_cart', JSON.stringify({
                cart: this.cart,
                expiryTime: this.expiryTime
            }));
        }
    }

    hide() {
        this.floatingTarget.classList.add('d-none');
    }

    show() {
        this.floatingTarget.classList.remove('d-none');
    }

    toggle() {
        this.floatingTarget.classList.toggle('d-none');
    }

    remove(event) {
        const key = event.currentTarget.dataset.key;

        if (this.cart?.[key]) {
            delete this.cart?.[key];
            this.update();
            if (Object.keys(this.cart).length === 0) {
                this.expiryTime = null;
            }
        }
    }

    decrement(event) {
        const key = event.currentTarget.dataset.key;

        if (this.cart?.[key].quantity > 1) {
            this.cart[key].quantity--;
            this.update();
        }
    }

    increment(event) {
        const key = event.currentTarget.dataset.key;

        if (this.cart?.[key].quantity < 30) {
            this.cart[key].quantity++;
            this.update();
        }
    }

    beforeType(event) {
        if (event.data) {
            const value = event.data;
            const isNumeric = /^\d*$/.test(value);
            const key = event.target.dataset.key;

            if (isNumeric && key && value > 0 && value < 10) {
                this.cart[key].quantity = Number.parseInt(value);
                this.update();
            } else {
                event.preventDefault();
            }
        }
    }

    startCountdown() {
        if (this.expiryTime) {
            const updateCountdown = () => {
                const now = Date.now();
                const timeLeft = Math.max(0, this.expiryTime - now);

                if (timeLeft > 0) {
                    const minutes = Math.floor(timeLeft / 60000);
                    const seconds = Math.floor((timeLeft % 60000) / 1000);
                    const timeString = `${minutes}:${seconds.toString().padStart(2, '0')}`;
                    this.countdownTextTarget.textContent = this.translatedTextValue.replace('{{time}}', timeString);

                    setTimeout(updateCountdown, 1000);
                } else {
                    this.countdownTextTarget.textContent = "Expired";
                    this.expireCart();
                }
            };

            updateCountdown();
        } else {
            this.countdownTextTarget.textContent = "";
        }
    }

    isAnonymousCheckoutAllowed() {
        return Object.values(this.cart).every(item =>
            item.type === 'product' || item.type === 'coupon' || item.type === 'gift_coupon'
        );
    }

    expireCart() {
        this.cart = {};
        this.expiryTime = null;
        window.localStorage.removeItem('__floating_cart');
        this.update();
    }

    setupTeleport() {
        this.mediaQuery = window.matchMedia('(max-width: 992px)');
        this.handleResize = (e) => {
            const slot = document.getElementById(e.matches ? 'cart-slot-mobile' : 'cart-slot-desktop');
            if (slot && !slot.contains(this.element)) {
                slot.appendChild(this.element);
            }
        };
        this.mediaQuery.addEventListener('change', this.handleResize);
    }

    template(item) {
        return `
            <tr>
                <td class="fw-bold product-title">${item?.title || '---'} ${item?.group_code ? `(${item?.group_code})` : ''}</td>
                <td class="fw-semibold text-nowrap w-px">${item?.price || 0} &euro;</td>
                <td class="w-px">
                    <div class="quantity-input">
                        <button type="button" class="quantity-btn-before" data-action="floating-cart#decrement" data-key="${item?._key || '---'}">
                            <svg width="14" height="2" viewBox="0 0 14 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 1H13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                        <input
                            data-key="${item?._key || '---'}"
                            class="input"
                            type="text"
                            data-quantity-target="input"
                            data-action="beforeinput->floating-cart#beforeType"
                            value="${item?.quantity || 1}"
                            min="1"
                        >
                        <button type="button" class="quantity-btn-after" data-action="floating-cart#increment" data-key="${item?._key || '---'}">
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7 1V13M1 7H13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                    </div>
                </td>
                <td class="fw-semibold w-px price-cell">${(item?.quantity || 1) * (item?.price || 0)} &euro;</td>
                <td class="w-px p-0">
                    <button type="button" class="btn btn-icon" data-action="floating-cart#remove" data-key="${item?._key || '---'}">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2.5 5.00002H4.16667M4.16667 5.00002H17.5M4.16667 5.00002L4.16667 16.6667C4.16667 17.1087 4.34226 17.5326 4.65482 17.8452C4.96738 18.1578 5.39131 18.3334 5.83333 18.3334H14.1667C14.6087 18.3334 15.0326 18.1578 15.3452 17.8452C15.6577 17.5326 15.8333 17.1087 15.8333 16.6667V5.00002M6.66667 5.00002V3.33335C6.66667 2.89133 6.84226 2.4674 7.15482 2.15484C7.46738 1.84228 7.89131 1.66669 8.33333 1.66669H11.6667C12.1087 1.66669 12.5326 1.84228 12.8452 2.15484C13.1577 2.4674 13.3333 2.89133 13.3333 3.33335V5.00002M8.33333 9.16669V14.1667M11.6667 9.16669V14.1667" stroke="#EC453A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </td>
            </tr>
        `;
    }
}
